import React, { useEffect, useState } from 'react';
import { makeStyles } from 'styles/util';
import { useAppContext } from 'context/AppContext';
import PropTypes from 'prop-types';
import strings from 'localization/strings';
import bookingItemTypes from 'enums/bookingItemTypes';
import additionalServiceTypes from 'enums/additionalServiceTypes';
import { getAdditionalServiceById } from 'helpers/AdditionalServiceHelper';
import { createBookingItem, createFormBookingItem, maximumDateString } from 'logic/bookingLogic';
import arrayMutators from 'final-form-arrays';
import { startOfDay } from 'date-fns';
import { parseIsoDate } from 'helpers/DateHelper';
import { formatAmount } from 'helpers/StringHelper';
import { calculateSum } from 'helpers/ArrayHelper';
import { calculateVat } from 'helpers/MonetaryHelper';
import routes from 'routes';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Form from 'form/Form';
import { TextField } from 'mui-rff';
import { FieldArray } from 'react-final-form-arrays';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import MarginBox from 'common/MarginBox';
import Text from 'common/Text';
import Typography from '@material-ui/core/Typography';

const useStyles = makeStyles(({ theme }) => ({
    bookingItem: {
        display: 'flex',
        gap: theme.spacing(2),
        alignItems: 'center'
    },
    data: {
        flex: '1 1 auto'
    },
    deleteButton: {
        flex: '0 0 auto',
        minWidth: 'auto'
    }
}));

const LockSystemAdditionalUsersDialog = ({ booking, storageSite, open, onOk, onCancel }) => {
    const classes = useStyles();
    const { appContext } = useAppContext();
    const [initialValues, setInitialValues] = useState(undefined);

    useEffect(() => {
        if(open) {
            setInitialValues({
                bookingItems: lockSystemAdditionalUserBookingItems.map(bookingItem => createFormBookingItem(bookingItem, booking, storageSite, appContext))
            });
        }
    }, [open]);

    const lockSystemAdditionalUserBookingItems = booking.bookingItems
        .filter(o => o.type === bookingItemTypes.service.key &&
                     getAdditionalServiceById(storageSite.additionalServices, o.referenceId)?.type === additionalServiceTypes.lockSystemAdditionalUser.key &&
                     parseIsoDate(o.timePeriodPrices[o.timePeriodPrices.length - 1].endDate ?? maximumDateString) >= startOfDay(new Date()) // not terminated
        );

    const lockSystemAdditionalUserAdditionalService = storageSite.additionalServices.find(o => o.type === additionalServiceTypes.lockSystemAdditionalUser.key);

    if(!lockSystemAdditionalUserAdditionalService) {
        return null;
    }

    const getLockSystemAdditionalUserAdditionalServicePriceIncludingVat = () => calculateSum(
        lockSystemAdditionalUserAdditionalService.timePeriodPrices.filter(tpp => tpp.isActive) ?? [],
        o => o.amount + calculateVat(o.amount, o.vatRate, appContext)
    );

    const information =
        strings.formatString(
            strings.manageLockSystemAdditionalUsersInformation,
            formatAmount(getLockSystemAdditionalUserAdditionalServicePriceIncludingVat(), booking.currency, appContext),
            booking.subscriptionBooking ? strings.manageLockSystemAdditionalUsersForSubscriptionBookingInformation : '',
            routes.contactUs
        );

    const handleFormSubmit = values => {
        onOk(values.bookingItems.map(formBookingItem => createBookingItem(formBookingItem, storageSite, appContext)));
    };

    const handleAddBookingItem = fields => {
        const bookingItem = {
            id: undefined,
            referenceId: lockSystemAdditionalUserAdditionalService.id,
            data: '',
            timePeriodPrices: [] // createFormBookingItem requires it
        };
        fields.push(createFormBookingItem(bookingItem, booking, storageSite, appContext));
    };

    return (
        <Dialog fullWidth maxWidth="md" open={open} onClose={onCancel}>
            <Form
                initialValues={initialValues}
                mutators={arrayMutators}
                onSubmit={handleFormSubmit}
            >
                {({ values, invalid, handleSubmit }) => (
                    <form onSubmit={handleSubmit}>
                        <DialogTitle disableTypography>
                            <Typography variant="h5">
                                {strings.manageLockSystemAdditionalUsers}
                            </Typography>
                        </DialogTitle>
                        <DialogContent>
                            <MarginBox bottom={2}>
                                <Typography variant="body1">
                                    <Text html={information} />
                                </Typography>
                            </MarginBox>

                            <FieldArray name="bookingItems">
                                {({ fields }) => (
                                    <>
                                        {
                                            fields.map((name, index) => {
                                                const formBookingItem = fields.value[index];
                                                return (
                                                    <Box key={name} className={classes.bookingItem}>
                                                        <Box className={classes.data}>
                                                            <TextField
                                                                name={`${name}.data`}
                                                                variant="outlined"
                                                                {...formBookingItem.configuration.dataTextboxProps}
                                                                helperText={undefined}
                                                            />
                                                        </Box>
                                                    </Box>
                                                );
                                            })
                                        }
                                        <Button
                                            className={classes.addButton}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => handleAddBookingItem(fields)}
                                        >
                                            {strings.add}
                                        </Button>
                                    </>
                                )}
                            </FieldArray>
                        </DialogContent>
                        <DialogActions>
                            {
                                values.bookingItems.length > 0 &&
                                (
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={invalid}
                                    >
                                        {!booking.subscriptionBooking && values.bookingItems.some(o => !o.id) ? strings.goToPayment : strings.save}
                                    </Button>
                                )
                            }
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={onCancel}
                            >
                                {strings.cancel}
                            </Button>
                        </DialogActions>
                    </form>
                )}
            </Form>
        </Dialog>
    );
};

LockSystemAdditionalUsersDialog.propTypes = {
    booking: PropTypes.object.isRequired,
    storageSite: PropTypes.object.isRequired,
    open: PropTypes.bool.isRequired,
    onOk: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired
};

export default LockSystemAdditionalUsersDialog;
